import { useQuery } from '@tanstack/react-query'

import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { formatDateTime, formatInteger } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { services } from '@/services'
import type { SourceStatus } from '@/types/models'

const STATUS_BADGE: Record<SourceStatus, 'success' | 'destructive' | 'warning' | 'outline'> = {
  ok: 'success',
  schema_mismatch: 'warning',
  unreachable: 'destructive',
  not_configured: 'outline',
}

export function SourcesPage() {
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: queryKeys.systemSources(),
    queryFn: () => services.system.sources(),
    // Bağlantı sağlığı Cube'de değil, backend'de ölçülür; canlı abonelik yok (plan K7).
    refetchInterval: 30_000,
  })

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-lg font-semibold">{messages.sources.title}</h1>
        <p className="text-muted-foreground text-sm">{messages.sources.description}</p>
      </div>

      {isLoading ? (
        <Loading rows={4} />
      ) : isError ? (
        <ErrorState error={error} onRetry={() => void refetch()} />
      ) : !data || data.sources.length === 0 ? (
        <Empty />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {data.sources.map((source) => (
              <Card key={source.name}>
                <CardHeader className="flex-row items-center justify-between">
                  <CardTitle>{source.label}</CardTitle>
                  <Badge variant={STATUS_BADGE[source.status]}>
                    {messages.sources.status[source.status]}
                  </Badge>
                </CardHeader>
                <CardContent className="grid grid-cols-2 gap-1 text-sm">
                  <span className="text-muted-foreground">
                    {messages.sources.columns.latency_ms}
                  </span>
                  <span className="tabular text-right">{formatInteger(source.latency_ms)}</span>
                  <span className="text-muted-foreground">
                    {messages.sources.columns.checked_at}
                  </span>
                  <span className="text-right">{formatDateTime(source.checked_at)}</span>
                  {source.message && (
                    <>
                      <span className="text-muted-foreground">
                        {messages.sources.columns.message}
                      </span>
                      <span className="text-right">{source.message}</span>
                    </>
                  )}
                  {source.schema_issues.length > 0 && (
                    <>
                      <span className="text-muted-foreground">{messages.sources.schemaIssues}</span>
                      <ul className="text-destructive text-right">
                        {source.schema_issues.map((issue, index) => (
                          <li key={index}>
                            {issue.view}
                            {issue.column ? `.${issue.column}` : ''}
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
